import type { Document } from '@/store/parser'
import type { SearchQueryParams } from 'afpnews-api'
import { numberParagraphs } from 'afpnews-api'
import type { Shot } from 'afpnews-api'

// Bornes en secondes au format de la query (`t=<début>&end=<fin>`) pour que le
// modèle construise `[description](/doc/UNO?t=…&end=…)` sans recalculer les durées.
function formatShotsForAi(shots: Shot[]): string {
  const lines = shots.map(shot => {
    const head =
      `${shot.numero}. ${shot.start}-${shot.end} (t=${shot.startSec}&end=${shot.endSec}) ${shot.description}`.trimEnd()
    const quotes = shot.citations.map(c => `   "${c.text}"`)
    return [head, ...quotes].join('\n')
  })
  return `Shot list :\n${lines.join('\n')}`
}

export function formatDocForAi(doc: Document, fullText = false): string {
  const parts: string[] = []
  parts.push(`UNO: ${doc.id}`)
  parts.push(`AFPSHORTID: ${doc.shortId?.toUpperCase() || 'N/A'}`)
  if (doc.headline) parts.push(`Titre: ${doc.headline}`)
  if (doc.lang) parts.push(`Langue: ${doc.lang}`)
  if (doc.country?.name) parts.push(`Pays: ${doc.country.name}`)
  if (doc.creator) parts.push(`Auteur: ${doc.creator}`)
  if (doc.published)
    parts.push(`Publié: ${new Date(doc.published).toISOString()}`)
  if (doc.events?.length)
    parts.push(
      `Événements (eventId): ${doc.events.map(e => `${e.name} (${e.id})`).join(' | ')}`
    )

  if (doc.type === 'video' && doc.shots.length > 0) {
    parts.push(formatShotsForAi(doc.shots))
  } else if ('paragraphs' in doc && Array.isArray(doc.paragraphs)) {
    const paragraphs = fullText ? doc.paragraphs : doc.paragraphs.slice(0, 3)
    if (paragraphs.length > 0) parts.push(numberParagraphs(paragraphs))
  }

  return parts.join('\n')
}

// `product`/`topic` peuvent aussi être des objets `{ in, exclude }` (cf. AdditionalParamValue
// dans afpnews-api) : String() sur un objet donnerait "[object Object]", donc pas de coercition directe.
function formatParamValue(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number')
    return String(value)
  if (Array.isArray(value)) return value.map(v => String(v)).join(', ')
  return JSON.stringify(value)
}

export function serializeSearchParams(params: SearchQueryParams): string {
  const parts: string[] = []
  if (params.query) parts.push(`query: ${params.query}`)
  if (params.langs?.length) parts.push(`langs: ${params.langs.join(', ')}`)
  if (params.dateFrom) parts.push(`dateFrom: ${params.dateFrom}`)
  if (params.dateTo) parts.push(`dateTo: ${params.dateTo}`)
  if (params.product) parts.push(`product: ${formatParamValue(params.product)}`)
  if (params.topic) parts.push(`topic: ${formatParamValue(params.topic)}`)
  return parts.join('\n')
}
